import { useEffect, useState } from 'react'
import { X, KeyRound, AlertCircle } from 'lucide-react'
import { useRedeemKey } from '@/features/solution-browser/hooks/useActivationKey'
import {
  formatActivationKey,
  isCompleteActivationKey,
  getRedeemErrorMessage,
} from '@/features/solution-browser/utils/activationKey'
import type { TestSeriesResponse } from '../types'

interface ActivateSeriesDialogProps {
  userId: string
  series: TestSeriesResponse | null
  onClose: () => void
  onSuccess: (series: TestSeriesResponse) => void
}

export default function ActivateSeriesDialog({ userId, series, onClose, onSuccess }: ActivateSeriesDialogProps) {
  const [inputValue, setInputValue] = useState('')
  const [apiError, setApiError] = useState<string | null>(null)
  const mutation = useRedeemKey(userId)

  // Fresh form whenever the dialog opens for a series
  useEffect(() => {
    setInputValue('')
    setApiError(null)
  }, [series])

  if (!series) return null

  const isPending = mutation.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!series || !isCompleteActivationKey(inputValue)) return

    try {
      const result = await mutation.mutateAsync(inputValue)
      if (result.data?.success) {
        onSuccess(series)
      } else {
        setApiError(result.data?.message ?? result.message ?? 'Activation failed.')
      }
    } catch (err: unknown) {
      setApiError(getRedeemErrorMessage(err))
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !isPending && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="activate-series-title"
        className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          disabled={isPending}
          aria-label="Close"
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50">
              <KeyRound className="h-7 w-7 text-blue-600" />
            </div>
          </div>
          <h2 id="activate-series-title" className="text-lg font-bold text-gray-900">
            Activate {series.seriesName}
          </h2>
          <p className="text-sm text-gray-500">
            Enter the activation key printed in your book or sent to your email.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setApiError(null)
              setInputValue(formatActivationKey(e.target.value))
            }}
            placeholder="XXXX-XXXX"
            maxLength={9}
            autoFocus
            autoCapitalize="characters"
            spellCheck={false}
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-center font-mono text-xl tracking-widest placeholder:text-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {apiError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{apiError}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={!isCompleteActivationKey(inputValue) || isPending}
            className="w-full rounded-xl bg-blue-600 py-3 font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? 'Activating…' : 'Activate'}
          </button>
        </form>
      </div>
    </div>
  )
}
